/**
 * Machine Register gate — estate claim card → Activate (no embedded wizard).
 * Soft≠Live: Activate remains the claim seat; return_to brings you back.
 */
import _React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { AnimatePresence } from "framer-motion";
import { html, M, Ico, SPRING, SOFT, go, stop } from "./ui.js";
import { buildHandoffHref, safeReturnToken } from "./shell-wayfind.js";
import { setDirtySeat } from "./quality-runtime.js";

const STEPS = Object.freeze([
  { n: "01", k: "Claim", say: "Serial · ownership on Activate" },
  { n: "02", k: "Return", say: "Land back in this estate" },
  { n: "03", k: "Tune", say: "Name · link · board · skills" },
]);

function MachineRegisterGate({ onYes, onNo }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onNo();
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [onNo]);

  return html`
    <${AnimatePresence}>
      <${M.div}
        key="machine-register-gate"
        className="mh-reggate__scrim"
        role="presentation"
        initial=${{ opacity: 0 }}
        animate=${{ opacity: 1 }}
        exit=${{ opacity: 0 }}
        transition=${SOFT}
        onClick=${onNo}>
        <${M.aside}
          className="mh-reggate"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mh-reggate-title"
          data-cmc-machine-register="1"
          data-taste="ops"
          initial=${{ opacity: 0, y: 28, scale: 0.96 }}
          animate=${{ opacity: 1, y: 0, scale: 1 }}
          transition=${SPRING}
          onClick=${(e) => stop(e)}>
          <div className="mh-reggate__glow" aria-hidden="true"/>
          <header className="mh-reggate__hd">
            <div className="mh-reggate__copy">
              <p className="mh-reggate__k">Register</p>
              <h3 className="mh-reggate__title" id="mh-reggate-title">Claim a Sentinel unit</h3>
            </div>
            <button type="button" className="mh-reggate__x" onClick=${onNo} aria-label="Cancel">×</button>
          </header>

          <p className="mh-reggate__lede">
            New hardware is claimed on Activate. This estate stays for units you already own —
            after claim, you return here to finish the card.
          </p>

          <ol className="mh-reggate__rail" aria-label="What happens next">
            ${STEPS.map((s) => html`
              <li key=${s.n} className="mh-reggate__step">
                <em className="mh-reggate__n" aria-hidden="true">${s.n}</em>
                <span className="mh-reggate__step-copy">
                  <b>${s.k}</b>
                  <small>${s.say}</small>
                </span>
              </li>`)}
          </ol>

          <footer className="mh-reggate__ft">
            <button type="button" className="btn btn--ghost mh-reggate__stay" onClick=${onNo}>
              Stay here
            </button>
            <button type="button" className="btn btn--go mh-reggate__go" autofocus
              data-cmc-machine-register-confirm="1"
              onClick=${onYes}>
              Continue to Activate <${Ico} d="arrowR" w=${12}/>
            </button>
          </footer>
        <//>
      <//>
    <//>`;
}

/** Machine-only Register confirm. Leaves to Activate with return_to. */
export function confirmMachineRegister({ fromRoute = "/machine" } = {}) {
  const href = buildHandoffHref("/activate", {
    return_to: safeReturnToken(fromRoute) || "/machine",
  });
  return new Promise((resolve) => {
    const host = document.createElement("div");
    host.setAttribute("data-cmc-machine-register-host", "1");
    document.body.appendChild(host);
    const root = createRoot(host);
    const finish = (ok) => {
      try { root.unmount(); } catch (_) { /* ignore */ }
      try { host.remove(); } catch (_) { /* ignore */ }
      if (ok) {
        try { setDirtySeat(false); } catch (_) { /* ignore */ }
        go(href);
      }
      resolve(!!ok);
    };
    root.render(html`<${MachineRegisterGate}
      onYes=${() => finish(true)}
      onNo=${() => finish(false)}
    />`);
  });
}

export default confirmMachineRegister;
